/**
 * Trading Rules Type Definitions
 * 
 * Defines display names, descriptions, and metadata for each rule type
 * This replaces the need for a database table
 */

export const RULE_TYPE_DEFINITIONS = {
  max_trades_per_day: {
    displayName: 'Trade Count Limit',
    shortName: 'Hit Max Trades',
    category: 'frequency',
    description: 'Exceeded maximum trades per day'
  },
  outside_trading_window: {
    displayName: 'Trading Outside Window',
    shortName: 'Traded outside trading hours',
    category: 'timing',
    description: 'Trade placed outside allowed hours'
  },
  daily_loss_exceeded: {
    displayName: 'Daily Loss Limit',
    shortName: 'Hit Max Daily Loss',
    category: 'risk',
    description: 'Total daily loss exceeds threshold'
  },
  position_loss_exceeded: {
    displayName: 'Position Loss Limit',
    shortName: 'Hit Max Position Loss',
    category: 'risk',
    description: 'Single position loss exceeds threshold'
  },
  stop_loss_modified: {
    displayName: 'Stop Loss Modified',
    shortName: 'Stop Modified',
    category: 'behavior',
    description: 'Stop loss order modified after entry'
  },
  profit_target_modified: {
    displayName: 'Profit Target Modified',
    shortName: 'Target Modified',
    category: 'behavior',
    description: 'Profit target modified after entry'
  },
  max_loss_per_day: {
    displayName: 'Daily Loss Limit',
    shortName: 'Hit Max Daily Loss',
    category: 'risk',
    description: 'Total daily loss exceeds threshold'
  },
  profit_target_per_day: {
    displayName: 'Target Daily Profit',
    shortName: 'Hit Daily Profit Target',
    category: 'risk',
    // Milestone (not a hard break). UI may show "Hit … then fell below" when currentlyAboveTarget is false.
    description: 'Daily profit target reached (milestone; may later fall below)'
  },
  consecutive_loss_cooldown: {
    displayName: 'Consecutive Loss Cooldown',
    shortName: 'Consecutive Losses',
    category: 'behavior',
    description: 'Too many losing trades in a row'
  },
  max_loss_per_position: {
    displayName: 'Position Loss Limit',
    shortName: 'Hit Max Position Loss',
    category: 'risk',
    description: 'Single position loss exceeds threshold'
  },
  max_risk_per_trade: {
    displayName: 'Max Risk Per Trade',
    shortName: 'Hit Max Risk Per Trade',
    category: 'risk',
    description: 'Trade risk exceeds maximum risk per trade threshold'
  },
  trading_window: {
    displayName: 'Trading Outside Window',
    shortName: 'Traded outside trading hours',
    category: 'timing',
    description: 'Trade placed outside allowed hours'
  },
  trade_without_stop_loss: {
    displayName: 'Missing Stop Loss',
    shortName: 'Missing Stop Loss',
    category: 'behavior',
    description: 'New position opened without a stop loss'
  },
  require_stop_loss: {
    displayName: 'Missing Stop Loss',
    shortName: 'Missing Stop Loss',
    category: 'behavior',
    description: 'New position opened without a stop loss'
  },
  manual_lockout: {
    displayName: 'Manual Lockout',
    shortName: 'Manual Lockout',
    category: 'risk',
    description: 'Manual lockout is currently active'
  }
};

/**
 * Get display name for a rule type
 */
export const getRuleDisplayName = (ruleType, useShort = false) => {
  const def = RULE_TYPE_DEFINITIONS[ruleType];
  if (!def) return ruleType;
  return useShort ? def.shortName : def.displayName;
};

/**
 * Get category for a rule type
 */
export const getRuleCategory = (ruleType) => {
  return RULE_TYPE_DEFINITIONS[ruleType]?.category || 'other';
};

/**
 * Get all rule types by category
 */
export const getRuleTypesByCategory = () => {
  const byCategory = {
    frequency: [],
    timing: [],
    risk: [],
    behavior: []
  };
  
  Object.entries(RULE_TYPE_DEFINITIONS).forEach(([ruleType, def]) => {
    byCategory[def.category].push(ruleType);
  });
  
  return byCategory;
};
